/* The Dashboard (ADR 0030): the six dashboard widgets, their sizes and settings,
   the default layout for a person's access, and the layout's stored shape.

   A layout is an ordered list — a widget's place is its index; the CSS grid packs
   them densely (12 columns × 48px rows). Every setting the backend keeps is listed
   here too (database/dashboard_layout_db.py cleans with the same whitelist). */

import type { DashboardLayoutDoc, DashboardWidgetDoc } from '../api/client';
import type { MoneyMode } from '../components/Statistics/unified/model';

export const COLS = 12;
export const MAX_ROWS = 16;
export const ROW = 48;
export const GAP = 12;
export const MAX_WIDGETS = 30;
/** A widget's header bar height. */
export const HEADER_H = 40;
/** Below this width the board is one column, read only. */
export const PHONE_QUERY = '(max-width: 767px)';

export type WidgetType = 'kpi' | 'chart' | 'split' | 'accounts' | 'needs' | 'team';

/** 0 = Today, 1 = 7 days, 2 = 30 days. */
export type Period = 0 | 1 | 2;
export const PERIOD_DAYS: Record<Period, number> = { 0: 1, 1: 7, 2: 30 };

export const KPI_KEYS = ['rev', 'ai', 'airev', 'chats', 'rpc', 'ppv', 'tips'] as const;
export type KpiKey = (typeof KPI_KEYS)[number];
export type ChartMetric = 'rev' | 'ppv' | 'chats' | 'msgs';
export type ChartSplit = 'none' | 'seller' | 'type';
export const ACCOUNT_COLS = ['status', 'rev', 'ai', 'chats'] as const;
export type AccountCol = (typeof ACCOUNT_COLS)[number];
export const ATTENTION_KINDS = ['killed', 'signed_out', 'billing_suspended', 'plan_expiring', 'ai_paused', 'throttled'] as const;
export type AttentionKind = (typeof ATTENTION_KINDS)[number];

interface Common {
  /** Empty = the type's own (translated) title. */
  title: string;
  hdr: boolean;
}
export interface CfgByType {
  kpi: Common & { per: Period; metrics: KpiKey[]; delta: boolean };
  chart: Common & { metric: ChartMetric; split: ChartSplit; per: 1 | 2; summary: boolean };
  split: Common & { by: 'seller' | 'type' | 'account'; metric: 'rev' | 'chats'; style: 'bars' | 'bar'; per: Period };
  accounts: Common & { style: 'list' | 'tiles'; cols: AccountCol[]; sort: 'att' | 'rev' | 'name'; show: 'all' | 'att' | 'off'; per: Period };
  needs: Common & { inc: AttentionKind[]; urgent: boolean };
  team: Common & { rank: 'rev' | 'chats'; ai: boolean; per: Period };
}
export type AnyCfg = CfgByType[WidgetType];

export type Widget = { [T in WidgetType]: { id: string; type: T; w: number; h: number; cfg: CfgByType[T] } }[WidgetType];
export type WidgetOf<T extends WidgetType> = Extract<Widget, { type: T }>;

/** How each setting may vary: a fixed list of values, booleans, or an ordered pick of values. */
type Rule = { one: readonly (string | number)[] } | { bool: true } | { chips: readonly string[] };

interface TypeSpec<T extends WidgetType> {
  icon: string;
  min: [number, number];
  def: [number, number];
  cfg: CfgByType[T];
  rules: Partial<Record<keyof CfgByType[T], Rule>>;
  /** Needs Stats access (money): never offered without it. */
  money: boolean;
}

const PERIODS = [0, 1, 2] as const;

export const TYPES: { [T in WidgetType]: TypeSpec<T> } = {
  kpi: {
    icon: 'hash', min: [3, 2], def: [12, 2], money: true,
    cfg: { title: '', hdr: false, per: 0, metrics: ['rev', 'ai', 'chats', 'ppv'], delta: true },
    rules: { per: { one: PERIODS }, metrics: { chips: KPI_KEYS }, delta: { bool: true } },
  },
  chart: {
    icon: 'bar-chart-2', min: [4, 4], def: [12, 6], money: true,
    cfg: { title: '', hdr: true, metric: 'rev', split: 'seller', per: 1, summary: true },
    rules: { metric: { one: ['rev', 'ppv', 'chats', 'msgs'] }, split: { one: ['none', 'seller', 'type'] }, per: { one: [1, 2] }, summary: { bool: true } },
  },
  split: {
    icon: 'pie-chart', min: [3, 3], def: [4, 5], money: true,
    cfg: { title: '', hdr: true, by: 'type', metric: 'rev', style: 'bar', per: 1 },
    rules: { by: { one: ['seller', 'type', 'account'] }, metric: { one: ['rev', 'chats'] }, style: { one: ['bars', 'bar'] }, per: { one: PERIODS } },
  },
  accounts: {
    icon: 'users', min: [3, 3], def: [4, 7], money: false,
    cfg: { title: '', hdr: true, style: 'list', cols: ['status', 'rev'], sort: 'att', show: 'all', per: 0 },
    rules: { style: { one: ['list', 'tiles'] }, cols: { chips: ACCOUNT_COLS }, sort: { one: ['att', 'rev', 'name'] }, show: { one: ['all', 'att', 'off'] }, per: { one: PERIODS } },
  },
  needs: {
    icon: 'bell', min: [4, 3], def: [8, 7], money: false,
    cfg: { title: '', hdr: true, inc: [...ATTENTION_KINDS], urgent: false },
    rules: { inc: { chips: ATTENTION_KINDS }, urgent: { bool: true } },
  },
  team: {
    icon: 'award', min: [3, 3], def: [4, 5], money: true,
    cfg: { title: '', hdr: true, rank: 'rev', ai: true, per: 0 },
    rules: { rank: { one: ['rev', 'chats'] }, ai: { bool: true }, per: { one: PERIODS } },
  },
};

export const WIDGET_TYPES = Object.keys(TYPES) as WidgetType[];

/** Who may use which dashboard widgets: money ones need Stats access; Team also needs CRM access. */
export interface Access {
  stats: boolean;
  crm: boolean;
}

export function allowedTypes(access: Access): WidgetType[] {
  return WIDGET_TYPES.filter((t) => (!TYPES[t].money || access.stats) && (t !== 'team' || access.crm));
}

/** Whether a widget in the layout can show its data to this person. */
export function canShow(type: WidgetType, access: Access): boolean {
  return allowedTypes(access).includes(type);
}

export function newId(): string {
  return `w${Math.random().toString(36).slice(2, 8)}`;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

export function newWidget<T extends WidgetType>(type: T, patch: Partial<CfgByType[T]> = {}, size?: [number, number]): WidgetOf<T> {
  const spec = TYPES[type];
  const [w, h] = size ?? spec.def;
  return { id: newId(), type, w, h, cfg: { ...spec.cfg, ...patch } } as unknown as WidgetOf<T>;
}

/** The layout a person sees until they first press Done, and what Reset to default restores. */
export function defaultWidgets(access: Access): Widget[] {
  if (access.stats) {
    return [
      newWidget('chart', { metric: 'rev', split: 'seller', per: 1 }, [12, 6]),
      newWidget('needs', {}, [8, 7]),
      newWidget('accounts', { cols: ['status', 'rev'] }, [4, 7]),
    ];
  }
  return [newWidget('needs', {}, [8, 8]), newWidget('accounts', { cols: ['status'] }, [4, 8])];
}

function cleanCfg<T extends WidgetType>(type: T, raw: Record<string, unknown> | undefined): CfgByType[T] {
  const spec = TYPES[type] as TypeSpec<T>;
  const out: Record<string, unknown> = { ...spec.cfg };
  const src = raw ?? {};
  if (typeof src.title === 'string') out.title = src.title.slice(0, 60);
  if (typeof src.hdr === 'boolean') out.hdr = src.hdr;
  for (const [key, rule] of Object.entries(spec.rules) as [string, Rule][]) {
    const v = src[key];
    if ('bool' in rule) {
      if (typeof v === 'boolean') out[key] = v;
    } else if ('chips' in rule) {
      if (Array.isArray(v)) out[key] = v.filter((x, i) => rule.chips.includes(x as string) && v.indexOf(x) === i);
    } else if (rule.one.includes(v as string | number)) {
      out[key] = v;
    }
  }
  return out as unknown as CfgByType[T];
}

/** A stored widget made safe to render: known type, settings filled in from the defaults, size clamped. */
export function widgetFromDoc(doc: DashboardWidgetDoc): Widget | null {
  if (!(doc.type in TYPES)) return null;
  const type = doc.type as WidgetType;
  const [minW, minH] = TYPES[type].min;
  return {
    id: String(doc.id),
    type,
    w: clamp(Math.round(Number(doc.w) || minW), minW, COLS),
    h: clamp(Math.round(Number(doc.h) || minH), minH, MAX_ROWS),
    cfg: cleanCfg(type, doc.cfg),
  } as Widget;
}

export interface Board {
  money: MoneyMode;
  widgets: Widget[];
  /** Still the default layout (never pressed Done): it follows the newest default. */
  isDefault: boolean;
}

export function boardFromDoc(doc: DashboardLayoutDoc | null, access: Access): Board {
  const money: MoneyMode = doc?.money === 'net' ? 'net' : 'gross';
  if (!doc || !Array.isArray(doc.widgets)) return { money, widgets: defaultWidgets(access), isDefault: true };
  const widgets = doc.widgets.map(widgetFromDoc).filter((w): w is Widget => !!w).slice(0, MAX_WIDGETS);
  return { money, widgets, isDefault: false };
}

export function boardToDoc(board: Board): DashboardLayoutDoc {
  const doc: DashboardLayoutDoc = { v: 1, money: board.money };
  if (!board.isDefault) doc.widgets = board.widgets.map(({ id, type, w, h, cfg }) => ({ id, type, w, h, cfg: { ...cfg } }));
  return doc;
}

export function resize(widget: Widget, w: number, h: number): Widget {
  const [minW, minH] = TYPES[widget.type].min;
  return { ...widget, w: clamp(w, minW, COLS), h: clamp(h, minH, MAX_ROWS) };
}

/** A widget's pixel size from its cells: what its fitting logic works with. */
export function cellsToPx(cols: number, rows: number, colWidth: number) {
  return { width: colWidth * cols + GAP * (cols - 1), height: ROW * rows + GAP * (rows - 1) };
}

/** Bar chart splits that exist for a metric (Type is for Revenue only; chats have no split). */
export function chartSplits(metric: ChartMetric): ChartSplit[] {
  if (metric === 'rev') return ['none', 'seller', 'type'];
  if (metric === 'ppv') return ['none', 'seller'];
  return ['none'];
}

export function effectiveSplit(cfg: CfgByType['chart']): ChartSplit {
  return chartSplits(cfg.metric).includes(cfg.split) ? cfg.split : 'none';
}
